import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type ReactNode } from 'react';

import {
  documentOwnerLabels,
  documents,
  type DocumentDetail,
  type DocumentOwnerType,
  type DocumentRecord,
  type DocumentStorage,
  type DocumentType,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { ajudaDoRepositorio } from '@/shared/help/ajuda/documents';
import { formatDate, formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { statusLabel } from '@/shared/ui/statusLabels';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { DeletedDocuments } from './DeletedDocuments';
import { chaveDeEscrita } from './escritaDoDono';
import { enviarArquivo, formatBytes, recusaDoArquivo } from './envio';
import { UploadDocumentDialog } from './UploadDocumentDialog';

/**
 * Painel comum de documentos de um dono.
 *
 * E o painel de todo dono que tem documento: operacao, tomador, corretor, apolice,
 * endosso, segurado e seguradora. O OnPoint mostra, em cada um, "Documentos
 * disponiveis", "Documentos excluidos" e "Upload de documento"; o SeguraCore acrescenta
 * a exigencia, que existe antes do arquivo, e o historico de versoes.
 *
 * Quem nao le os documentos do dono nao ve o painel: a leitura geral do repositorio
 * nao vale com escopo de corretora, e a dos documentos da seguradora vale so para a
 * seguradora.
 */

/** Situacao do documento, com o tom que a lista usa. */
const situacoes: Record<
  string,
  { label: string; tone: 'warning' | 'success' | 'neutral' }
> = {
  pendente: { label: 'Pendente', tone: 'warning' },
  recebido: { label: 'Recebido', tone: 'success' },
  dispensado: { label: 'Dispensado', tone: 'neutral' },
};

/** Tipos oferecidos ao dono, para a escolha e para o rotulo da lista. */
function useTiposDoDono(ownerType: DocumentOwnerType, habilitado: boolean) {
  return useQuery({
    queryKey: ['document-types', 'dono', ownerType],
    queryFn: ({ signal }) =>
      documents.types({ owner_type: ownerType, limit: 100 }, signal),
    enabled: habilitado,
    staleTime: 5 * 60 * 1000,
    // O catalogo so decora a lista. Sem ele, o painel continua funcionando.
    retry: false,
  });
}

export function DocumentsPanel({
  ownerType,
  ownerId,
  title = 'Documentos',
  description = 'Exigencia, envio e historico de versoes.',
  extraAction,
  onDeleted,
  allowDelete = false,
  deleteOnlyWithFile = false,
  showDeleted,
  allowUpload = true,
  allowRequire = true,
  group,
  emptyTitle = 'Nenhum documento exigido',
  emptyDescription = 'Liste aqui o que o dono precisa receber. A exigencia existe antes do arquivo.',
  pipeCardId,
}: {
  ownerType: DocumentOwnerType;
  ownerId: string;
  title?: string;
  description?: string;
  /** Acao a mais no cabecalho. */
  extraAction?: ReactNode;
  /** Avisa a tela quando um documento sai, para a lista de excluidos. */
  onDeleted?: () => void;
  /**
   * Oferece a exclusao. Na operacao, so com `deleteOnlyWithFile`: o documento exigido
   * e parte da esteira, e exclui-lo antes do arquivo apagaria o que falta antes da
   * emissao (para tirar a exigencia, dispensar).
   */
  allowDelete?: boolean;
  /**
   * A exclusao vale so para o documento com arquivo ("Documentos inseridos" da cotacao
   * do OnPoint, P-COT-3). O so exigido continua sendo dispensado, e nao excluido.
   */
  deleteOnlyWithFile?: boolean;
  /** Mostra a lista "Documentos excluidos". Segue a exclusao, salvo quando dito. */
  showDeleted?: boolean;
  /** Oferece o "Upload de documento", que nomeia e envia de uma vez. */
  allowUpload?: boolean;
  /** Oferece o "Exigir documento", que registra o que falta antes do arquivo. */
  allowRequire?: boolean;
  /** Grupo do documento enviado pelo "Upload de documento". */
  group?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  /**
   * Cartao da esteira de onde o documento e exigido (P-EST-1): a exigencia guarda o
   * cartao de origem ("Documentos solicitados" da gaveta).
   */
  pipeCardId?: string;
}) {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [exigindo, setExigindo] = useState(false);

  // A corretora le os documentos pedidos a ela (P-COR-1); o servidor troca o
  // vinculo pelo da sessao, e o documento de outra corretora nao chega.
  const podeLer =
    can('documentos.documento.view') ||
    (ownerType === 'seguradora' && can('documentos.seguradora.view')) ||
    (ownerType === 'corretor' && can('documentos.corretora.view'));
  const podeExigir = can(chaveDeEscrita(ownerType, 'documentos.documento.create'));
  const comExcluidos = showDeleted ?? allowDelete;

  const consulta = useQuery({
    queryKey: ['documents', { ownerType, ownerId }],
    queryFn: ({ signal }) =>
      documents.list({ owner_type: ownerType, owner_id: ownerId, limit: 100 }, signal),
    enabled: podeLer,
  });

  const armazenamento = useQuery({
    queryKey: ['document-storage'],
    queryFn: ({ signal }) => documents.storage(signal),
    staleTime: 5 * 60 * 1000,
    enabled: podeLer,
  });

  const tipos = useTiposDoDono(ownerType, can('documentos.documento.view'));
  const nomeDoTipo = new Map(
    (tipos.data?.items ?? []).map((tipo) => [tipo.id, tipo.name]),
  );

  if (!podeLer) return null;

  const itens = consulta.data?.items ?? [];

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader
          title={title}
          description={description}
          action={
            podeExigir || extraAction ? (
              <div className="flex flex-wrap items-center gap-2">
                {extraAction}
                {allowUpload ? (
                  <UploadDocumentDialog
                    ownerType={ownerType}
                    ownerId={ownerId}
                    group={group ?? documentOwnerLabels[ownerType]}
                  />
                ) : null}
                {podeExigir && allowRequire ? (
                  <Button variant="secondary" onClick={() => setExigindo(true)}>
                    Exigir documento
                  </Button>
                ) : null}
              </div>
            ) : null
          }
        />

        <CardBody className="flex flex-col gap-3">
          {consulta.isError ? (
            <Alert tone="danger" title="Nao foi possivel carregar os documentos">
              {consulta.error.message}
            </Alert>
          ) : null}

          {armazenamento.data && !armazenamento.data.available ? (
            <Alert tone="info" title="Envio de arquivo indisponivel nesta instalacao">
              {armazenamento.data.missing_requirement} Ate la, os documentos continuam
              sendo exigidos, versionados e auditados normalmente.
            </Alert>
          ) : null}

          {consulta.isPending ? (
            <p className="text-sm text-ink-500">Carregando os documentos.</p>
          ) : itens.length === 0 ? (
            <EmptyState icon="file" title={emptyTitle} description={emptyDescription} />
          ) : (
            <ul className="flex flex-col divide-y divide-line">
              {itens.map((document) => (
                <DocumentRow
                  key={document.id}
                  document={document}
                  typeName={
                    document.document_type_id
                      ? nomeDoTipo.get(document.document_type_id)
                      : undefined
                  }
                  types={tipos.data?.items ?? []}
                  storage={armazenamento.data}
                  onChanged={() => {
                    void queryClient.invalidateQueries({ queryKey: ['documents'] });
                  }}
                  onDeleted={() => {
                    void queryClient.invalidateQueries({
                      queryKey: ['document-deletions'],
                    });
                    onDeleted?.();
                  }}
                  allowDelete={
                    allowDelete && (!deleteOnlyWithFile || document.current_version > 0)
                  }
                />
              ))}
            </ul>
          )}
        </CardBody>

        {exigindo ? (
          <RequireDialog
            open
            ownerType={ownerType}
            ownerId={ownerId}
            types={tipos.data?.items ?? []}
            pipeCardId={pipeCardId}
            onClose={() => setExigindo(false)}
          />
        ) : null}
      </Card>

      {comExcluidos ? (
        <DeletedDocuments ownerType={ownerType} ownerId={ownerId} />
      ) : null}
    </div>
  );
}

function DocumentRow({
  document,
  typeName,
  types,
  storage,
  onChanged,
  onDeleted,
  allowDelete,
}: {
  document: DocumentRecord;
  typeName: string | undefined;
  types: DocumentType[];
  storage: DocumentStorage | undefined;
  onChanged: () => void;
  onDeleted: () => void;
  allowDelete: boolean;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const entrada = useRef<HTMLInputElement>(null);
  const [enviando, setEnviando] = useState(false);
  const [detalhe, setDetalhe] = useState(false);
  const [editando, setEditando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);

  // A corretora envia o arquivo dos documentos pedidos a ela (P-COR-1).
  const podeEnviar =
    can(chaveDeEscrita(document.owner_type, 'documentos.documento.create')) ||
    (document.owner_type === 'corretor' && can('documentos.corretora.create'));
  const podeEditar = can(
    chaveDeEscrita(document.owner_type, 'documentos.documento.update'),
  );
  const podeExcluir =
    allowDelete &&
    can(chaveDeEscrita(document.owner_type, 'documentos.documento.delete'));
  const situacao = situacoes[document.status] ?? {
    label: document.status,
    tone: 'neutral' as const,
  };

  const dispensar = useMutation({
    mutationFn: (waived: boolean) =>
      documents.update(document.id, {
        group: document.group,
        title: document.title,
        required: document.required,
        waived,
      }),
    onSuccess: () => {
      toast.success('Documento atualizado.');
      onChanged();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const baixar = useMutation({
    mutationFn: () => documents.download(document.id),
    onSuccess: (link) => {
      window.open(link.url, '_blank', 'noopener,noreferrer');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const excluir = useMutation({
    mutationFn: () => documents.remove(document.id),
    onSuccess: () => {
      toast.success('Documento excluido.');
      setExcluindo(false);
      onChanged();
      onDeleted();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  async function enviar(arquivo: File) {
    if (!storage) return;

    const recusa = recusaDoArquivo(arquivo, storage);
    if (recusa) {
      toast.error(recusa);
      return;
    }

    setEnviando(true);
    try {
      const resultado = await enviarArquivo(document.id, arquivo, storage);
      toast.success('Arquivo enviado.');
      if (resultado.avisoDeCota) toast.info(resultado.avisoDeCota);
      onChanged();
    } catch (erro) {
      toast.error(erro instanceof Error ? erro.message : 'Falha ao enviar o arquivo.');
    } finally {
      setEnviando(false);
      if (entrada.current) entrada.current.value = '';
    }
  }

  const detalhes = [
    document.group || 'Sem grupo',
    // Origem e pedido ao corretor (P-COT-3): quem inseriu e quem pediu.
    document.origin
      ? `origem ${statusLabel('origemDocumento', document.origin)}`
      : null,
    document.requested_at
      ? `pedido ao corretor em ${formatDate(document.requested_at)}${
          document.requested_label ? ` por ${document.requested_label}` : ''
        }`
      : null,
    document.created_at ? `inserido em ${formatDate(document.created_at)}` : null,
    document.current_version > 0 ? `versao ${document.current_version}` : null,
    document.valid_until ? `validade ${formatDate(document.valid_until)}` : null,
  ].filter(Boolean);

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-medium text-ink-900">{document.title}</p>
          {document.required ? <Badge tone="neutral">Obrigatorio</Badge> : null}
          <Badge tone={situacao.tone}>{situacao.label}</Badge>
          {typeName ? <Badge tone="neutral">{typeName}</Badge> : null}
          {document.expired ? <Badge tone="danger">Vencido</Badge> : null}
        </div>
        <p className="text-2xs text-ink-500">{detalhes.join(' · ')}</p>
        {document.status_note ? (
          <p className="mt-0.5 text-2xs text-ink-600">{document.status_note}</p>
        ) : null}
        {document.request_note ? (
          <p className="mt-0.5 text-2xs text-ink-600">
            Pedido: {document.request_note}
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {document.current_version > 0 ? (
          <>
            <Button variant="secondary" onClick={() => setDetalhe(true)}>
              Versoes
            </Button>
            <Button
              variant="secondary"
              loading={baixar.isPending}
              onClick={() => baixar.mutate()}
            >
              Baixar
            </Button>
          </>
        ) : null}

        {podeEditar && document.current_version === 0 ? (
          <Button
            variant="secondary"
            loading={dispensar.isPending}
            onClick={() => dispensar.mutate(document.status !== 'dispensado')}
          >
            {document.status === 'dispensado' ? 'Voltar a exigir' : 'Dispensar'}
          </Button>
        ) : null}

        {podeEditar ? (
          <Button variant="ghost" onClick={() => setEditando(true)}>
            Editar
          </Button>
        ) : null}

        {podeEnviar && storage?.available ? (
          <>
            <input
              ref={entrada}
              type="file"
              className="hidden"
              aria-label={`Arquivo de ${document.title}`}
              accept={Object.keys(storage.accepted_types).join(',')}
              onChange={(event) => {
                const arquivo = event.target.files?.[0];
                if (arquivo) void enviar(arquivo);
              }}
            />
            <Button loading={enviando} onClick={() => entrada.current?.click()}>
              {document.current_version > 0 ? 'Substituir' : 'Enviar'}
            </Button>
          </>
        ) : null}

        {podeExcluir ? (
          <Button variant="danger" onClick={() => setExcluindo(true)}>
            Excluir
          </Button>
        ) : null}
      </div>

      <Modal
        open={excluindo}
        onClose={() => setExcluindo(false)}
        title="Excluir documento"
        description={`${document.title}: o documento sai da lista e vai para os excluidos, com a data e quem excluiu. A configuracao do repositorio diz se o arquivo fica guardado para restaurar.`}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setExcluindo(false)}>
              Voltar
            </Button>
            <Button
              variant="danger"
              loading={excluir.isPending}
              onClick={() => excluir.mutate()}
            >
              Excluir documento
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-600">Deseja realmente excluir o documento?</p>
      </Modal>

      {detalhe ? (
        <VersionsDialog
          documentId={document.id}
          title={document.title}
          open
          onClose={() => setDetalhe(false)}
        />
      ) : null}

      {editando ? (
        <EditDialog
          document={document}
          types={types}
          onClose={() => setEditando(false)}
          onSaved={() => {
            setEditando(false);
            onChanged();
          }}
        />
      ) : null}
    </li>
  );
}

function VersionsDialog({
  documentId,
  title,
  open,
  onClose,
}: {
  documentId: string;
  title: string;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();

  const consulta = useQuery({
    queryKey: ['document', documentId],
    queryFn: ({ signal }) => documents.get(documentId, signal),
  });

  const baixar = useMutation({
    mutationFn: (version: number) => documents.download(documentId, version),
    onSuccess: (link) => {
      window.open(link.url, '_blank', 'noopener,noreferrer');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const detalhe: DocumentDetail | undefined = consulta.data;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Versoes de ${title}`}
      description="Substituir gera nova versao. A anterior continua disponivel: ela pode ser a que foi assinada."
      size="md"
      footer={<Button onClick={onClose}>Fechar</Button>}
    >
      {consulta.isPending ? (
        <p className="text-sm text-ink-500">Carregando as versoes.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-line">
          {(detalhe?.versions ?? []).map((versao) => (
            <li
              key={versao.id}
              className="flex flex-wrap items-center justify-between gap-3 py-3"
            >
              <div className="min-w-0">
                <p className="font-medium text-ink-900">
                  v{versao.version} {versao.filename}
                </p>
                {versao.display_name ? (
                  <p className="text-2xs text-ink-700">{versao.display_name}</p>
                ) : null}
                <p className="text-2xs text-ink-500">
                  {formatBytes(versao.byte_size)}
                  {versao.uploaded_label ? ` · ${versao.uploaded_label}` : ''}
                  {` · ${formatDateTime(versao.uploaded_at)}`}
                </p>
                {versao.comment ? (
                  <p className="text-2xs text-ink-600">{versao.comment}</p>
                ) : null}
                <p className="truncate font-mono text-2xs text-ink-400">
                  {versao.checksum}
                </p>
              </div>

              {versao.state === 'disponivel' ? (
                <Button
                  variant="secondary"
                  loading={baixar.isPending}
                  onClick={() => baixar.mutate(versao.version)}
                >
                  Baixar
                </Button>
              ) : (
                <Badge tone="warning">Envio nao confirmado</Badge>
              )}
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

/** Opcoes do tipo: os ativos oferecidos, mais o atual, mesmo inativo. */
function opcoesDeTipo(types: DocumentType[], atual?: string | null) {
  return [
    { value: '', label: 'Sem tipo' },
    ...types
      .filter((tipo) => tipo.active || tipo.id === atual)
      .map((tipo) => ({
        value: tipo.id,
        label: tipo.active ? tipo.name : `${tipo.name} (inativo)`,
      })),
  ];
}

function EditDialog({
  document,
  types,
  onClose,
  onSaved,
}: {
  document: DocumentRecord;
  types: DocumentType[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [titulo, setTitulo] = useState(document.title);
  const [grupo, setGrupo] = useState(document.group);
  const [obrigatorio, setObrigatorio] = useState(document.required);
  const [tipo, setTipo] = useState(document.document_type_id ?? '');
  const [nota, setNota] = useState(document.status_note ?? '');
  const [validade, setValidade] = useState(document.valid_until ?? '');

  const salvar = useMutation({
    mutationFn: () =>
      documents.update(document.id, {
        group: grupo,
        title: titulo,
        required: obrigatorio,
        document_type_id: tipo,
        status_note: nota,
        valid_until: validade,
      }),
    onSuccess: () => {
      toast.success('Documento atualizado.');
      onSaved();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Editar documento"
      description="Tipo, descricao da situacao e validade acompanham o documento. Nenhuma regra os usa."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Voltar
          </Button>
          <Button
            loading={salvar.isPending}
            disabled={titulo.trim() === ''}
            onClick={() => salvar.mutate()}
          >
            Salvar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field
          label="Titulo"
          required
          value={titulo}
          onChange={(event) => setTitulo(event.target.value)}
        />
        <Field
          label="Grupo"
          value={grupo}
          onChange={(event) => setGrupo(event.target.value)}
        />
        <Select
          label="Tipo de documento"
          options={opcoesDeTipo(types, document.document_type_id)}
          value={tipo}
          help={ajudaDoRepositorio['Tipo de documento']}
          onChange={(event) => setTipo(event.target.value)}
        />
        <Textarea
          label="Descricao da situacao"
          value={nota}
          rows={3}
          maxLength={2000}
          help={ajudaDoRepositorio['Descricao da situacao']}
          onChange={(event) => setNota(event.target.value)}
        />
        <Field
          label="Validade"
          type="date"
          value={validade}
          help={ajudaDoRepositorio.Validade}
          onChange={(event) => setValidade(event.target.value)}
        />
        <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            checked={obrigatorio}
            onChange={(event) => setObrigatorio(event.target.checked)}
            className="size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
          />
          Documento obrigatorio
        </label>
      </div>
    </Modal>
  );
}

function RequireDialog({
  ownerType,
  ownerId,
  types,
  open,
  onClose,
  pipeCardId,
}: {
  ownerType: DocumentOwnerType;
  ownerId: string;
  types: DocumentType[];
  open: boolean;
  onClose: () => void;
  pipeCardId?: string | undefined;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [titulo, setTitulo] = useState('');
  const [grupo, setGrupo] = useState('');
  const [tipo, setTipo] = useState('');
  const [obrigatorio, setObrigatorio] = useState(true);

  const exigir = useMutation({
    mutationFn: () =>
      documents.require({
        owner_type: ownerType,
        owner_id: ownerId,
        group: grupo,
        title: titulo,
        required: obrigatorio,
        ...(tipo ? { document_type_id: tipo } : {}),
        ...(pipeCardId ? { pipe_card_id: pipeCardId } : {}),
      }),
    onSuccess: () => {
      toast.success('Documento exigido.');
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Exigir documento"
      description="A exigencia existe antes do arquivo: a esteira precisa saber o que falta."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Voltar
          </Button>
          <Button
            loading={exigir.isPending}
            disabled={titulo.trim() === ''}
            onClick={() => exigir.mutate()}
          >
            Exigir documento
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field
          label="Titulo"
          required
          value={titulo}
          placeholder="Contrato social consolidado"
          onChange={(event) => setTitulo(event.target.value)}
        />
        <Field
          label="Grupo"
          value={grupo}
          placeholder="Tomador"
          hint="Agrupa a exigencia na lista, como Tomador ou Beneficiario."
          onChange={(event) => setGrupo(event.target.value)}
        />
        {types.some((item) => item.active) ? (
          <Select
            label="Tipo de documento"
            options={opcoesDeTipo(types)}
            value={tipo}
            help={ajudaDoRepositorio['Tipo de documento']}
            onChange={(event) => setTipo(event.target.value)}
          />
        ) : null}
        <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            checked={obrigatorio}
            onChange={(event) => setObrigatorio(event.target.checked)}
            className="size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
          />
          Documento obrigatorio
        </label>
      </div>
    </Modal>
  );
}
